import {useDispatch, useSelector} from 'react-redux'
import {useMemo, useCallback, useEffect} from 'react'
import {useQuery, gql} from '@apollo/client'
import useRouteBasedSkip from '../../hooks/useRouteBasedSkip'
import {
  setStates as setStatesAction,
  setLoadingStates as setLoadingStatesAction,
  setErrorLoadingStates as setErrorLoadingStatesAction,
  updateDefaultState as updateDefaultStateAction,
  updateDefaultChartType as updateDefaultChartTypeAction,
  updateDefaultCounties as updateDefaultCountiesAction,
  selectStates,
  selectLoadingStates,
  selectErrorLoadingStates,
  selectStatesList
} from '../slices/longitudinalStatesSlice'

const LONGITUDINAL_STATE_LIST_QUERY = gql`
  query longitudinalStateList {
    longitudinalStateList {
      STATE_NAME
      STATE_ABBREVIATION
    }
  }
`

export const useLongitudinalStates = () => {
  const dispatch = useDispatch()
  const states = useSelector(selectStates)
  const loadingStates = useSelector(selectLoadingStates)
  const errorLoadingStates = useSelector(selectErrorLoadingStates)
  const statesList = useSelector(selectStatesList)
  const shouldSkip = useRouteBasedSkip('/timeseries')

  const {
    data: apolloStates,
    loading: apolloLoadingStates,
    refetch: refetchStates,
    error: apolloErrorLoadingStates
  } = useQuery(LONGITUDINAL_STATE_LIST_QUERY, {
    context: {clientName: 'longitudinal'},
    skip: shouldSkip
  })

  // Sync Apollo query results to Redux
  useEffect(() => {
    dispatch(setStatesAction(apolloStates))
  }, [apolloStates, dispatch])

  useEffect(() => {
    dispatch(setLoadingStatesAction(apolloLoadingStates))
  }, [apolloLoadingStates, dispatch])

  useEffect(() => {
    dispatch(setErrorLoadingStatesAction(apolloErrorLoadingStates))
  }, [apolloErrorLoadingStates, dispatch])

  const updateDefaultState = useCallback(
    (chartName, value) => {
      dispatch(updateDefaultStateAction({chartName, value}))
    },
    [dispatch]
  )

  const updateDefaultChartType = useCallback(
    (chartName) => {
      dispatch(updateDefaultChartTypeAction(chartName))
    },
    [dispatch]
  )

  const updateDefaultCounties = useCallback(
    (chartName, counties) => {
      dispatch(updateDefaultCountiesAction({chartName, counties}))
    },
    [dispatch]
  )

  return useMemo(
    () => ({
      states,
      loadingStates,
      refetchStates,
      errorLoadingStates,
      updateDefaultState,
      updateDefaultChartType,
      statesList,
      updateDefaultCounties
    }),
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      states,
      loadingStates,
      errorLoadingStates,
      statesList[0].currentRiskOverTimeCounties,
      statesList[0].currentPOTCounties,
      statesList[0].chartType,
      statesList[0].riskOverTimeStateAbbreviation,
      statesList[0].potStateAbbreviation,
      updateDefaultChartType,
      updateDefaultState,
      updateDefaultCounties
    ]
    /* eslint-enable react-hooks/exhaustive-deps */
  )
}
